import React from "react";
import ServicePageLayout, { H2, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "UniFi Installer Lethbridge | Ubiquiti Network Installation Alberta | Epicom Systems",
  metaDescription:
    "UniFi / Ubiquiti network design and installation across Southern Alberta. UniFi gateways, switching, Wi-Fi, Protect video and Access — engineered as one integrated environment for businesses, farms and acreages.",
  canonicalPath: "/unifi-installation",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/unifi-installation#service",
    "name": "UniFi / Ubiquiti Installation",
    "serviceType": "Ubiquiti UniFi network design and installation",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Design and installation of Ubiquiti UniFi networks — gateways, switching, Wi-Fi, Protect video, and Access — for businesses, farms and acreages across Southern Alberta.",
    "url": "https://epicom.ca/unifi-installation",
  },
};

export default function UniFiInstallation() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="UniFi / Ubiquiti"
      h1="UniFi Design & Installation for Southern Alberta"
      intro="Epicom Systems designs and installs Ubiquiti UniFi network environments end to end — gateways, switching, wireless, video and access control — engineered to work as one system rather than a pile of parts. If your business runs on the network, it deserves to be built like it."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/business-wifi", label: "Business Wi-Fi" },
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/cellular-das", label: "Cellular DAS" },
      ]}
    >
      <H2>What we design & install</H2>
      <UL>
        <><strong className="text-white/90">UniFi gateways / routing</strong> — Dream Machines and Cloud Gateways sized appropriately for the site, with VLANs, guest isolation, VPN, and IDS/IPS where it makes sense.</>
        <><strong className="text-white/90">UniFi switching</strong> — PoE and non-PoE, from 8-port utility switches to enterprise aggregation. Cabling, terminations and patch panels done properly.</>
        <><strong className="text-white/90">UniFi Wi-Fi</strong> — access point selection and placement based on the actual building and use case, not marketing datasheets. See our <a href="/business-wifi" className="text-signal hover:underline">Business Wi-Fi</a> page for depth.</>
        <><strong className="text-white/90">UniFi Protect</strong> — IP camera systems with local NVR, PoE cabling, remote viewing and no ongoing per-camera cloud fees.</>
        <><strong className="text-white/90">UniFi Access</strong> — electronic locks, readers, and door controllers integrated with the same UniFi environment.</>
      </UL>

      <H2>Why one integrated environment matters</H2>
      <P>
        A patchwork of network gear from four different vendors — one brand for the router,
        another for the switch, a third for Wi-Fi, a fourth for cameras — technically works,
        but every one of those pieces has its own management portal, its own login, its own
        firmware cycle and its own support case number when something goes wrong. When
        UniFi is designed as a single environment, everything shows up in one interface,
        one client list, one set of logs and one place to make a change. For the person
        actually running the business, that is a real operational win.
      </P>

      <H2>Where we work</H2>
      <UL>
        <>Small and medium businesses that have outgrown their consumer router / mesh setup</>
        <>Farms and acreages that need reliable network coverage across multiple buildings</>
        <>Industrial and commercial shops with mixed office, service bay and yard coverage</>
        <>Multi-building sites where wireless links between buildings need to feed into the same network</>
        <>Home Wi-Fi overhauls where the client wants business-grade equipment and coverage that actually works</>
      </UL>

      <H2>How we work with UniFi</H2>
      <P>
        Epicom designs and installs using Ubiquiti UniFi products because, in our experience,
        they hit the right combination of capability, price and long-term maintainability for
        the kind of work we do. We are an experienced installer of these systems — not a
        Ubiquiti-authorized dealer, certified partner or reseller. We buy through normal
        channels and pass the same pricing model through to you. If your requirements are
        genuinely better served by different gear on a given job, we will tell you.
      </P>

      <H2>A realistic starting point</H2>
      <P>
        Most UniFi projects start with a site walk and a real conversation about what the
        network needs to do — how many users, how many buildings, what applications, what’s
        already there. From there we scope the system, quote it honestly and schedule the
        install around your operation.
      </P>
    </ServicePageLayout>
  );
}
